import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Option } from "space-monad";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import {
  AddressAttachmentType,
  IdentityAttachmentType,
  useAddressAttachmentType,
  useIdentityAttachmentType,
} from "../../../../store/common/models";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { VerificationService } from "../../../../store/verification/service";

export const useKycUpdate = (
  item: VerificationDTO.Responses.GetSingleKyc.T
) => {
  const idAttType = useIdentityAttachmentType();
  const addAttType = useAddressAttachmentType();

  const [isEditing, setIsEditing] = useState(false);

  const [idDocType, setIdDocType] = useState<IdentityAttachmentType>();

  useEffect(() => {
    setIdDocType(
      Option(item.serviceDetails)
        .map((x) => x.find(({ service }) => service === "DOCUMENT"))
        .map((x) => x.details.documents)
        .map((x) => x[0])
        .map((x) => x.type as IdentityAttachmentType)
        .get()
    );
  }, [item.serviceDetails, idAttType]);

  const [addDocType, setAddDocType] = useState<AddressAttachmentType>();
  useEffect(() => {
    setAddDocType(
      Option(item.serviceDetails)
        .map((x) => x.find(({ service }) => service === "ADDRESS"))
        .map((x) => x.details.documents)
        .map((x) => x[0])
        .map((x) => x.type as AddressAttachmentType)
        .get()
    );
  }, [item.serviceDetails, addAttType]);

  const router = useRouter();

  const {
    labels,
    validation,
  } = VerificationDTO.Requests.UpdateIndividualKYC.useFields();

  const defaultValues = useMemo(
    () => VerificationDTO.Requests.UpdateIndividualKYC.mapFromSingleKyc(item),
    [item]
  );

  const form = useForm<VerificationDTO.Requests.UpdateIndividualKYC.T>({
    defaultValues,
    resolver: yupResolver(validation),
  });

  const loader = useAppLoader();

  const submit = useCallback(
    (data: VerificationDTO.Requests.UpdateIndividualKYC.T) =>
      loader(
        Promise.all([
          VerificationService.updateIndividualKyc(item.fraudCheckId, data),
          ...(addDocType
            ? Option(item.serviceDetails)
                .map((x) => x.find(({ service }) => service === "ADDRESS"))
                .map((x) => x.details.documents)
                .map((x) =>
                  x.map((y) =>
                    VerificationService.updateDocumentType(
                      item.fraudCheckId,
                      y.id,
                      { type: addDocType }
                    )
                  )
                )
                .getOrElse([])
            : []),
          ...(idDocType
            ? Option(item.serviceDetails)
                .map((x) => x.find(({ service }) => service === "DOCUMENT"))
                .map((x) => x.details.documents)
                .map((x) =>
                  x.map((y) =>
                    VerificationService.updateDocumentType(
                      item.fraudCheckId,
                      y.id,
                      { type: idDocType }
                    )
                  )
                )
                .getOrElse([])
            : []),
        ])
          .then(() => router.stateService.reload())
          .catch(noop)
      ),
    [item.fraudCheckId, router, item.serviceDetails, addDocType, idDocType]
  );

  return {
    isEditing,
    setIsEditing,
    form,
    labels,
    onSubmit: form.handleSubmit(submit),
    idDocType,
    setIdDocType,
    addDocType,
    setAddDocType,
  };
};
